import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { TOOL_BY_SLUG, REBECA_ALLOWED_ROUTES } from "../../../data/workforceOsTools";
import { ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { SUBMIT_RESUME_URL } from "../../../data/recruitingLinks";

/**
 * Generic MVP / Coming-Soon page for Workforce OS tools that don't ship a
 * full interactive workflow yet. Renders the tool's name, value prop, and
 * "Join early access" + "Talk to TRITAL Care" CTAs. Reads from TOOL_BY_SLUG.
 */
export default function WorkforceOsToolPlaceholder() {
  const { slug } = useParams();
  const tool = TOOL_BY_SLUG[slug];

  React.useEffect(() => {
    if (tool) document.title = `${tool.name} | TRITAL Workforce OS™`;
  }, [tool]);

  if (!tool) return <Navigate to="/workforce-os" replace />;

  const showRebeca = REBECA_ALLOWED_ROUTES.some((re) => re.test(`/workforce-os/${slug}`));

  return (
    <WorkforceOsLayout
      breadcrumb={tool.name}
      title={tool.name}
      description={tool.tagline}
    >
      <section className="py-10" data-testid={`wos-placeholder-${tool.slug}`}>
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 lg:p-10">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-trital-navy text-white text-[11px] font-bold tracking-wider uppercase">
              <Sparkles className="w-3 h-3" strokeWidth={2} /> {tool.status}
            </div>
            <h2 className="mt-5 font-display font-black text-2xl sm:text-3xl text-trital-navy leading-tight">
              What this tool delivers
            </h2>
            <ul className="mt-4 space-y-2.5 text-[14.5px] text-slate-700 leading-relaxed">
              {[
                `Built for ${tool.audience}.`,
                "Free during MVP / preview.",
                "Compliance-first, healthcare-aware.",
                "Integrated with the TRITAL Workforce OS™ tool catalog.",
                "Final review always performed by a TRITAL Care recruiter.",
              ].map((b) => (
                <li key={b} className="flex gap-2"><span className="text-trital-red">›</span>{b}</li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trital-navy text-white font-semibold btn-lift"
                data-testid="placeholder-cta-early-access"
              >
                Join Early Access <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold btn-lift"
                data-testid="placeholder-cta-talk"
              >
                Talk to TRITAL Care
              </Link>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h4 className="font-display font-bold text-trital-navy">In the meantime</h4>
              <p className="mt-2 text-[13.5px] text-slate-600">Free TRITAL Care tools you can use today:</p>
              <div className="mt-4 space-y-2">
                <Link to="/workforce-os/resume-formatter" className="block px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy" data-testid="placeholder-alt-resume">
                  Healthcare Resume Formatter
                </Link>
                <Link to="/workforce-os/ratelab" className="block px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy" data-testid="placeholder-alt-ratelab">
                  TRITAL RateLab™
                </Link>
                <Link to="/workforce-os/submittal-score" className="block px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy" data-testid="placeholder-alt-submittal">
                  TRITAL Submittal Score™
                </Link>
                <Link to="/workforce-os/clinician-passport" className="block px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy" data-testid="placeholder-alt-passport">
                  TRITAL Clinician Passport™
                </Link>
                <Link to="/careers/jobs" className="block px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy" data-testid="placeholder-alt-jobs">
                  Search Healthcare Jobs
                </Link>
                <a
                  href={SUBMIT_RESUME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-[13.5px] font-semibold text-trital-navy"
                  data-testid="placeholder-alt-resume-submit"
                >
                  Submit Resume <ExternalLink className="w-3 h-3 opacity-70" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
      {showRebeca && <RebecaMiguelChatbot variant="floating" />}
    </WorkforceOsLayout>
  );
}
